import { notifications } from '@mantine/notifications'
import type { PayloadFindRequestParams } from '@opsopti/shared/src/services/api/api.types'
import type { MRT_PaginationState } from 'mantine-react-table'

import * as commonClasses from '@/styles/componentCommons.css'
import type { GetRoutePath, RouteNames } from '@/utils/navigation'
import { getRouteByName, getRoutePath } from '@/utils/navigation'

export type CommonAction = {
  label: string
  onClick?: () => void
  /**
   * Link url/path.
   * This will also be used to determine the `current` state of the link if UI supports it.
   */
  to?: GetRoutePath | string
  /**
   * Additional paths to consider as current.
   */
  additionalCurrentRoutes?: RouteNames[]
}

export type CommonActions = CommonAction[]

export function getCommonActionPath(to?: CommonAction['to']) {
  if (!to) return
  if (typeof to === 'string') return to
  return getRoutePath(to.name, to.params as any)
}

export function getCommonActionRoute(to?: CommonAction['to']) {
  if (!to) return
  if (typeof to === 'string') return
  return getRouteByName(to.name)
}

export function getFormScreenTitle(id: string | null, entity: string, override?: string): string {
  const isNew = id === null

  const prefix = isNew ? `New` : 'Edit'

  if (override) {
    return `${prefix}: ${override}`
  }

  return `${prefix} ${entity}`
}

export function getFormSubmitLabel(id: string | null, entity: string): string {
  const isNew = id === null

  return isNew ? `Create New ${entity}` : `Update ${entity}`
}

export function showErrorNotification(data: { title?: string; message?: string }) {
  notifications.show({
    title: data.title || 'Something went wrong.',
    message: data.message || 'There was an issue with this action. Please try again or contact support.',
    color: 'red',
    classNames: commonClasses.notificationFilled,
    position: 'bottom-left',
  })
}

export function showSuccessNotification(data: { title?: string; message?: string }) {
  if (data.title || data.message) {
    notifications.show({
      title: data.title,
      message: data.message,
      color: 'green',
      classNames: commonClasses.notificationFilled,
      position: 'bottom-left',
    })
  }
}

export function castPaginationStateToQueryParams(pagination: MRT_PaginationState): PayloadFindRequestParams {
  return {
    limit: String(pagination.pageSize),
    page: String(pagination.pageIndex + 1),
  }
}

export function clickCoordinate(x: number, y: number) {
  const ev = new MouseEvent('click', {
    view: window,
    bubbles: true,
    cancelable: true,
    screenX: x,
    screenY: y,
  })

  const el = document.elementFromPoint(x, y)

  el?.dispatchEvent(ev)
}
